import { ColDef } from 'ag-grid-community';
import { ENTITLEMENT_NEW_THRESHOLD_DAYS } from '../common-constants';
import { AllocatedEntitlementStatus, EntitlementStatus } from '../models/entitlement-status.model';
import { TrialLinkStatus, TrialStatus, UserAssignedTrialStatus } from '../models/trial-status.model';
import {
  AgServiceDetailsRendererParams,
  AgServiceDetailsRowParams,
  AgServiceDetailsViewModel,
} from './ag-renderers/ag-service-details/ag-service-details.component';
import { AgStatusPresentationMap } from './ag-renderers/ag-status-renderer/ag-status-renderer.component';
import {
  AgTimeFormatComponent,
  AgTimeFormatRendererParams,
} from './ag-renderers/ag-time-format/ag-time-format.component';
import { AgBundleDetailsComponent } from './ag-renderers/home-ui/ag-bundle-details/ag-bundle-details.component';
import { DateTimeColumnOptions, greyOutCellStyle, SharedGridColumnDefs } from './grid.utils';

interface TrialExpiryStatusRow {
  status?: string | null;
}

export const HOME_TRIAL_STATUS_PRESENTATIONS: AgStatusPresentationMap = {
  [TrialStatus.ACTIVE]: { label: 'Active', iconClass: 'ra-icon-ide-md-color-check' },
  [TrialStatus.ENDED]: { label: 'Ended', iconClass: 'ra-icon-ide-md-color-unprovisioned' },
  [TrialStatus.PROVISIONING]: {
    label: 'Needs action',
    iconClass: 'ra-icon-ide-md-color-alarm-medium-priority',
    tooltip:
      'Set up for one or more trial items needs to be completed from within that application. Go to the Hub Home to open.',
  },
};

export const HOME_USER_ASSIGNED_TRIAL_STATUS_PRESENTATIONS: AgStatusPresentationMap = {
  [UserAssignedTrialStatus.ACTIVE]: { label: 'Active', iconClass: 'ra-icon-ide-md-color-check' },
  [UserAssignedTrialStatus.EXPIRED]: { label: 'Expired', iconClass: 'ra-icon-ide-md-color-unprovisioned' },
};

export const HOME_ENTITLEMENT_STATUS_PRESENTATIONS: AgStatusPresentationMap = {
  [EntitlementStatus.ACTIVE]: { label: 'Active', iconClass: 'ra-icon-ide-md-color-check' },
  [EntitlementStatus.COMPLETED]: {
    label: 'Expired',
    iconClass: 'ra-icon-ide-md-color-unprovisioned',
    className: 'muted',
  },
  [EntitlementStatus.CANCELLED]: {
    label: 'Cancelled',
    iconClass: 'ra-icon-ide-md-color-unprovisioned',
    className: 'muted',
    tooltip: 'This entitlement has been cancelled by an administrator.',
  },
  [TrialLinkStatus.TRIALEXPIRED]: {
    label: 'Expired',
    iconClass: 'ra-icon-ide-md-color-unprovisioned',
  },
};

export const HOME_ALLOCATED_ENTITLEMENT_STATUS_PRESENTATIONS: AgStatusPresentationMap = {
  [AllocatedEntitlementStatus.ACTIVE]: { label: 'Active', iconClass: 'ra-icon-ide-md-color-check' },
  [AllocatedEntitlementStatus.PENDING]: { label: 'Pending', iconClass: 'ra-icon-ide-md-auto-refresh' },
  [AllocatedEntitlementStatus.COMPLETED]: {
    label: 'Expired',
    iconClass: 'ra-icon-ide-md-color-unprovisioned',
    className: 'muted',
  },
  [AllocatedEntitlementStatus.CANCELLED]: {
    label: 'Cancelled',
    iconClass: 'ra-icon-ide-md-color-unprovisioned',
    className: 'muted',
    tooltip: 'This entitlement has been cancelled by an administrator.',
  },
};

export interface HomeServiceDetailsRow {
  readonly detailServiceDataHeaders?: { readonly header?: string | null } | null;
  readonly quantity?: number | null;
  readonly productFamily?: string | null;
  readonly catalogNumber?: string | null;
  readonly entitlementType?: string | null;
  readonly primaryEmail?: string | null;
  readonly ownerEmail?: string | null;
  readonly isTrialEntitlement?: boolean | null;
  readonly status?: string | null;
  readonly effectiveDate?: Date | string | number | null;
  readonly consumed?: number | null;
}

export interface HomeServiceDetailsOptions {
  readonly isUserEntitlement: boolean;
  readonly highlightNewItems: boolean;
}

export function createHomeServiceDetailsViewModel(
  options: HomeServiceDetailsOptions,
): (data: HomeServiceDetailsRow | null | undefined) => AgServiceDetailsViewModel {
  return (data) => {
    const daysSinceEffective =
      (Date.now() - new Date(data?.effectiveDate as Date | string | number).getTime()) / (3600 * 24 * 1000);

    return {
      header: data?.detailServiceDataHeaders?.header,
      purchasedQuantity: options.isUserEntitlement ? data?.quantity : undefined,
      productFamily: data?.productFamily,
      catalogNumber: data?.catalogNumber,
      entitlementType: data?.entitlementType,
      party: options.isUserEntitlement ? { label: 'Assigned To', value: data?.primaryEmail } : undefined,
      showAllocatedOn: false,
      showTrialBadge: Boolean(data?.isTrialEntitlement),
      showNewMarker:
        options.highlightNewItems && daysSinceEffective <= ENTITLEMENT_NEW_THRESHOLD_DAYS && !data?.consumed,
      muted: data?.status === EntitlementStatus.CANCELLED || data?.status === EntitlementStatus.COMPLETED,
    };
  };
}

const ACTIVE_TRIAL_STATUS = TrialStatus.ACTIVE.toLowerCase();
const REDEEMED_LINK_STATUS = TrialLinkStatus.REDEEMED;

function isTrialExpirySoonEligible(data?: TrialExpiryStatusRow | null): boolean {
  const status = data?.status;
  if (!status) {
    return false;
  }
  return status === REDEEMED_LINK_STATUS || status.toLowerCase() === ACTIVE_TRIAL_STATUS;
}

export class HomeGridColumnDefs extends SharedGridColumnDefs {
  static serviceDetailsParams(
    isUserEntitlement: boolean,
    highlightNewItems: boolean,
  ): Pick<AgServiceDetailsRendererParams<HomeServiceDetailsRow>, 'toViewModel' | 'enableRowExpansion'> {
    const toViewModel = createHomeServiceDetailsViewModel({ isUserEntitlement, highlightNewItems });
    return {
      toViewModel: (params: AgServiceDetailsRowParams<HomeServiceDetailsRow>) => toViewModel(params.data),
      enableRowExpansion: true, // Enables toggling the master row to open/close the nested Allocation ID grid in Home UI
    };
  }

  static override dateTimeCol(field: string, headerName: string, options: DateTimeColumnOptions = {}): ColDef {
    return {
      ...super.dateTimeCol(field, headerName, options),
      cellRenderer: AgTimeFormatComponent,
    };
  }

  /**
   * Reusable "Expiring Soon" badge configuration for date columns whose data has a
   * `status` field indicating an active trial or redeemed link. Application status
   * semantics are kept out of the shared renderer via this typed predicate.
   */
  static expireSoonBadgeParams<TData extends TrialExpiryStatusRow = TrialExpiryStatusRow>(): Pick<
    AgTimeFormatRendererParams<TData>,
    'showExpireSoonFlag' | 'isExpiringSoonEligible'
  > {
    return {
      showExpireSoonFlag: true,
      isExpiringSoonEligible: isTrialExpirySoonEligible,
    };
  }

  static trialNameCol(fieldName: string): ColDef {
    return this.extend(
      this.cellRendererCol(fieldName, 'Trial', AgBundleDetailsComponent, {
        cellRendererParams: { highlightNewItems: true },
      }),
      { enableValue: true, minWidth: 350 },
    );
  }

  static trialDescriptionCol(): ColDef {
    return this.extend(this.textCol('description', 'Description', { filterable: false, sortable: false }), {
      cellStyle: greyOutCellStyle,
      minWidth: 350,
    });
  }

  static trialRedeemedOnCol(): ColDef {
    return this.extend(this.dateTimeCol('redeemedOn', 'Redeemed'), {
      cellStyle: greyOutCellStyle,
      initialSort: 'desc',
      sortingOrder: ['desc', 'asc'],
    });
  }

  static trialExpiresOnCol(): ColDef {
    return this.extend(this.dateTimeCol('expiresOn', 'Expiry Date'), {
      cellStyle: greyOutCellStyle,
      cellRendererParams: this.expireSoonBadgeParams(),
    });
  }
}
